<script>
export default {
  name: 'UhShare',
};
</script>
<script setup>
import { computed, ref } from 'vue';
import { useClipboard } from '@vueuse/core';

import UhModal from './UhModal';
import UhShareItem from './UhShareItem';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  url: {
    type: String,
    required: true,
  },
  heading: {
    type: String,
    required: false,
    default: null,
  },
  image: {
    type: [Boolean, String],
    required: false,
    default: false,
  },
  email: {
    type: Object,
    required: false,
    default: null,
  },
  whatsapp: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const open = ref(false);

const nativeShare = async () => {
  if (!hasNativeShare.value) {
    return;
  }
  try {
    await navigator.share({
      title: props.title,
      url: props.url,
    });
  } catch (e) {
    /* empty */
  }
};

const { copy, copied, isSupported } = useClipboard({
  source: props.url,
});

const hasNativeShare = computed(() => {
  return navigator.share !== undefined;
});

const buttonClickHandler = async () => {
  if (hasNativeShare.value) {
    await nativeShare();
    return;
  }
  open.value = true;
};

const emailShareLink = computed(
  () =>
    `mailto:?subject=${props.email.subject}&body=${
      props.email.body !== null ? props.title + ' ' + props.url : ''
    }`
);

const whatsAppShareLink = computed(
  () => `https://api.whatsapp.com/send?text=${props.title + ' ' + props.url}`
);
</script>

<template>
  <div class="tw-flex">
    <div class="tw-basis-full" @click="buttonClickHandler">
      <template v-if="$slots.button">
        <slot name="button"></slot>
      </template>
      <template v-else>
        <div
          class="tw-flex tw-items-center tw-gap-x-1 tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-primary-grey tw-shadow-sm"
        >
          <i class="fal fa-share-alt"></i>
          <label>Share</label>
        </div>
      </template>
    </div>
    <UhModal
      :show="open"
      width="400px"
      min-height="100px"
      :blur="false"
      @close="open = false"
    >
      <template #header>
        <span class="tw-font-semibold">{{ props.heading ?? 'Share' }}</span>
      </template>
      <template #body>
        <div class="tw-flex tw-flex-col tw-gap-y-3">
          <slot name="modal-body"></slot>
          <div
            v-show="props.image"
            class="tw-aspect-[16/9] tw-w-full tw-rounded tw-bg-gray-200 tw-bg-cover tw-bg-center tw-object-cover"
            :style="{ backgroundImage: `url(${props.image})` }"
          ></div>
          <UhShareItem v-if="props.email && props.email.subject">
            <a
              :href="emailShareLink"
              class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-x-2 group-active:tw-text-primary-blue-dark"
            >
              <i class="far fa-envelope"></i>
              <span>Email</span>
            </a>
          </UhShareItem>
          <UhShareItem v-if="props.whatsapp">
            <a
              :href="whatsAppShareLink"
              class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-x-2 tw-text-teal-600 group-hover:tw-text-sky-500 group-active:tw-text-primary-blue-dark"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-whatsapp"></i>
              <span>WhatsApp</span>
            </a>
          </UhShareItem>
          <UhShareItem v-if="isSupported">
            <button
              type="button"
              class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-gap-x-2 group-active:tw-text-primary-blue-dark"
              @click="copy()"
            >
              <template v-if="copied">
                <i class="fas fa-copy"></i>
                <span>Link copied!</span>
              </template>
              <template v-else>
                <i class="fal fa-copy"></i>
                <span>Copy link</span>
              </template>
            </button>
          </UhShareItem>
        </div>
      </template>
    </UhModal>
  </div>
</template>
